import { useContext, useEffect, useState } from 'react';
import {
  Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import { IAddGroupItem, ISurveyQuestion } from '../../../shared/services/survey/survey.model';

interface Props {
  api?: { addGroupItem: (groupId: string, payload: any) => Promise<any> };
  open: boolean;
  onClose: () => void;
  successHandler: () => void;
  groupId: string;
  availableQuestions: ISurveyQuestion[];
  nextSortOrder: number;
  targetPageNumber?: number;
}

export const AddGroupItemDialog = ({ open, onClose, successHandler, groupId, availableQuestions, nextSortOrder, targetPageNumber = 1 , api}: Props) => {
  const surveyAdminService = SurveyAdminService.getInstance();
  const userNotificationsContext = useContext(UserNotificationsContext);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [surveyQuestionId, setSurveyQuestionId] = useState('');
  const [sortOrder, setSortOrder] = useState(nextSortOrder);
  const [requiredOverride, setRequiredOverride] = useState(false);
  const [hasRequiredOverride, setHasRequiredOverride] = useState(false);

  useEffect(() => {
    if (open) {
      setSurveyQuestionId(availableQuestions[0]?.id ?? '');
      setSortOrder(nextSortOrder);
      setRequiredOverride(false);
      setHasRequiredOverride(false);
    }
  }, [open, availableQuestions, nextSortOrder]);

  const handleSubmit = async () => {
    if (!surveyQuestionId) return;
    setIsSubmitting(true);
    try {
      const payload: IAddGroupItem = {
        surveyQuestionId,
        sortOrder,
        pageNumber: targetPageNumber,
        ...(hasRequiredOverride ? { requiredOverride } : {}),
      };
      await (api?.addGroupItem ?? ((g: string, pl: any) => surveyAdminService.addGroupItem(g, pl)))(groupId, payload);
      userNotificationsContext.openSnackbar({ open: true, message: 'Question added', backgroundColor: 'primary.dark' });
      successHandler();
      onClose();
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'An error occurred, please try again', backgroundColor: 'text.primary' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title='Add Question'
      cancelHandler={onClose}
      submitHandler={handleSubmit}
      isSubmitting={isSubmitting}
      disableSaveButton={!surveyQuestionId}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        {availableQuestions.length === 0 ? (
          <Typography color='text.secondary'>No questions available. Create questions in the Question Library first.</Typography>
        ) : (
          <FormControl fullWidth>
            <InputLabel>Question</InputLabel>
            <Select
              value={surveyQuestionId}
              label='Question'
              onChange={e => setSurveyQuestionId(e.target.value)}
            >
              {availableQuestions.map(q => (
                <MenuItem key={q.id} value={q.id}>
                  {q.prompt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <TextField
          label='Sort order'
          type='number'
          value={sortOrder}
          onChange={e => setSortOrder(Number(e.target.value))}
          fullWidth
        />
        <FormControlLabel
          control={<Checkbox checked={hasRequiredOverride} onChange={e => setHasRequiredOverride(e.target.checked)} />}
          label='Override required setting'
        />
        {hasRequiredOverride && (
          <FormControlLabel
            control={<Checkbox checked={requiredOverride} onChange={e => setRequiredOverride(e.target.checked)} />}
            label='Required in this survey'
          />
        )}
      </Stack>
    </DialogWrapper>
  );
};
